// frontend/modules/customer-category/views/CustomerCategoryManagementView.tsx
import React, { useEffect, useRef } from 'react';
import {
    Card,
    Typography,
    Button,
    Space,
    Table,
    Tag,
    Tooltip,
    Modal
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/common/ThemeProvider';
import { useCustomerCategories } from '../hooks/useCustomerCategories';
import { useGroup } from '@/modules/nub-user/modules/user-group/hooks/use-group';
import { CustomerCategoryDTO } from '@/shared/api/model';

const { Title } = Typography;
const { confirm } = Modal;

const CustomerCategoryManagementView: React.FC = () => {
    const { dark } = useTheme();
    const navigate = useNavigate();

    const {
        categories,
        isLoading,
        // error,
        fetchCategories,
        deleteCategory
    } = useCustomerCategories();

    const {
        groups: userGroups
    } = useGroup();

    // Use ref to store the latest fetchCategories function to avoid dependency issues
    const fetchCategoriesRef = useRef(fetchCategories);
    fetchCategoriesRef.current = fetchCategories;

    useEffect(() => {
        fetchCategoriesRef.current();
    }, []); // Only run once on mount, use ref for function call

    const handleCreateClick = () => {
        navigate('/admin/general-config/customer-categories/create');
    };

    const handleEditClick = (id: number) => {
        navigate(`/admin/general-config/customer-categories/${id}/edit`);
    };

    const handleViewClick = (id: number) => {
        navigate(`/admin/general-config/customer-categories/${id}`);
    };

    const handleDeleteClick = (category: CustomerCategoryDTO) => {
        confirm({
            title: `Are you sure you want to delete the category "${category.name}"?`,
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone',
            okText: 'Yes, delete',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                deleteCategory(category.id!);
            }
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: CustomerCategoryDTO) => (
                <a onClick={() => handleViewClick(record.id!)}>{text}</a>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
                    {status}
                </Tag>
            )
        },
        {
            title: 'Products',
            key: 'products',
            render: (_:any, record: CustomerCategoryDTO) => (
                <span>{record.products?.length} products</span>
            )
        },
        {
            title: 'User Groups',
            key: 'userGroups',
            render: (_:any, record: CustomerCategoryDTO) => {
                if (record.userGroups?.length === 0) {
                    return <span>No groups</span>;
                }
                
                const groupNames = record.userGroups?.map(ug => {
                    const group = userGroups.find(g => g.code === ug.groupCode);
                    return group ? group.name : ug.groupCode;
                });
                
                return (
                    <div>
                        {groupNames?.slice(0, 2).map((name, index) => (
                            <Tag key={index} style={{ marginBottom: 2 }}>
                                {name}
                            </Tag>
                        ))}
                        {groupNames && groupNames.length > 2 && (
                            <Tooltip title={groupNames?.slice(2).join(', ')}>
                                <Tag>+{groupNames.length - 2} more</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_:any, record: CustomerCategoryDTO) => (
                <Space size="small">
                    <Tooltip title="View">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => handleViewClick(record.id!)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick(record.id!)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteClick(record)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <Title level={2} className={dark ? 'text-white' : ''}>
                    Customer Categories
                </Title>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateClick}
                >
                    Create Category
                </Button>
            </div>

            <Card className={dark ? 'bg-gray-800' : ''} loading={isLoading}>
                <Table
                    dataSource={categories}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default CustomerCategoryManagementView;